import { useTranslation, Trans } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { systemApi } from '@/api/system';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useWorkspace } from '@/workspace/WorkspaceContext';

export function System(): JSX.Element {
  const { t } = useTranslation();
  const { currentBotId } = useWorkspace();

  const system = useQuery({
    queryKey: ['bot', 'system', currentBotId],
    queryFn: systemApi.get,
    enabled: !!currentBotId,
  });

  if (!currentBotId) {
    return (
      <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
        {t('system.empty')}
      </div>
    );
  }
  if (system.isLoading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (system.error || !system.data) return <p className="text-sm text-destructive">{t('system.loadError')}</p>;
  const data = system.data;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('system.title')}</h1>
        <p className="text-sm text-muted-foreground">
          <Trans
            i18nKey="system.subtitle"
            values={{ bot: data.bot?.name ?? currentBotId }}
            components={{ b: <span className="font-mono" /> }}
          />
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{t('system.runtimeTitle')}</CardTitle>
          <CardDescription>{t('system.runtimeSubtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-x-6 gap-y-1 text-sm md:grid-cols-2">
          <Row k={t('system.rows.version')} v={data.version} />
          <Row k={t('system.rows.nodeEnv')} v={data.nodeEnv} />
          <Row k={t('system.rows.bot')} v={data.bot ? `${data.bot.name} (${data.bot.botId})` : '—'} />
          <Row k={t('system.rows.botStatus')} v={data.bot ? (data.bot.enabled ? t('common.active') : t('common.disabled')) : '—'} />
          <Row k={t('system.rows.llm')} v={`${data.llm.provider} / ${data.llm.model}`} />
          <Row
            k={t('system.rows.rag')}
            v={data.rag ? t('system.rows.ragWithKb', { provider: data.rag.provider, kb: data.rag.knowledgeBaseId ?? t(`integrations.form.rag.statuses.${data.rag.status}`) }) : t('system.rows.ragNotConfigured')}
          />
          <Row k={t('system.rows.chatHistoryTurns')} v={String(data.chat.maxHistoryTurns)} />
          <Row
            k={t('system.rows.responseCache')}
            v={data.cache.enabled ? t('system.rows.on') : t('system.rows.off')}
          />
          <Row k={t('system.rows.responseCacheDiagnose')} v={describeCacheEndpoint(data.cache.diagnose, t)} />
          <Row k={t('system.rows.responseCacheSupport')} v={describeCacheEndpoint(data.cache.support, t)} />
          <Row
            k={t('system.rows.responseCacheStats')}
            v={t('system.rows.responseCacheStatsValue', {
              defaultValue: '{{hits}} hits / {{misses}} misses',
              hits: data.cache.hits,
              misses: data.cache.misses,
            })}
          />
          <Row k={t('system.rows.auditRetention')} v={String(data.retention.auditDays)} />
          <Row k={t('system.rows.feedbackRetention')} v={String(data.retention.feedbackDays)} />
          <Row k={t('system.rows.chatOpsRetention')} v={String(data.retention.chatOpsDays)} />
          <Row k={t('system.rows.chatSupportRetention')} v={String(data.retention.chatSupportDays)} />
          <Row k={t('system.rows.corsOrigins')} v={data.dashboardOrigins.join(', ')} />
        </CardContent>
      </Card>
    </div>
  );
}

function describeCacheEndpoint(
  endpoint: { enabled: boolean; ttlSeconds: number },
  t: TFunction,
): string {
  if (!endpoint.enabled) return t('system.rows.off');
  return t('system.rows.responseCacheTtl', { defaultValue: '{{ttl}} s', ttl: endpoint.ttlSeconds });
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b py-1 last:border-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="truncate font-mono text-xs">{v}</span>
    </div>
  );
}
